import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { Flame, Target, PencilLine, Check } from 'lucide-react';
import { apiGet } from '../api';
import { useWritingActivity } from '../hooks/useWritingActivity';
import { useEmbedTheme } from '../hooks/useEmbedTheme';
import { computeMetrics, DayDelta } from '../utils/writingMetrics';
import '../App.css';

interface BookLite { entityId: string; title: string; updatedAt?: string; coverUrl?: string }

const bareId = (entityId: string) => entityId.replace(/^BOOK#/, '');
const nf = (n: number) => n.toLocaleString();
/** Signed, with a true minus sign: a day below the previous total shows how far below. */
const signed = (n: number) => (n < 0 ? `−${nf(-n)}` : nf(n));

// Theme-aware colors (resolve against .writing-dashboard's CSS vars).
const SURFACE = 'var(--wd-surface)';
const BORDER = 'var(--wd-border)';
const INK = 'var(--wd-ink)';
const INK_SOFT = 'var(--wd-ink-soft)';
const INK_FAINT = 'var(--wd-ink-faint)';
const TRACK = 'var(--wd-track)';
const ACCENT = 'var(--wd-accent)';
const GREEN = 'var(--wd-green)';
const NEG = 'var(--wd-neg)';

const cardStyle: React.CSSProperties = {
  background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px',
};
const labelStyle: React.CSSProperties = {
  fontSize: '.72rem', letterSpacing: '.1em', textTransform: 'uppercase', color: INK_SOFT, fontWeight: 700,
};
// Read by screen readers, not shown.
const srOnly: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

// ── Stat card ────────────────────────────────────────────────────────────────
/** `below` explains a negative count. Its text is in INK, which keeps small type readable on
 *  the light theme, and screen readers hear the count unsigned: "minus 48 words below
 *  where you started today" would mean 48 above. */
const StatCard: React.FC<{ label: string; value: number; accent?: string; below?: { unit: string; note: string } }> = ({ label, value, accent, below }) => (
  <div style={{ ...cardStyle, flex: '1 1 150px', minWidth: 0 }}>
    <div style={labelStyle}>{label}</div>
    <div aria-hidden={below ? true : undefined}
      style={{ fontSize: '2.1rem', fontWeight: 700, lineHeight: 1.1, marginTop: 8, color: accent ?? INK, fontVariantNumeric: 'tabular-nums' }}>{signed(value)}</div>
    {below ? (
      <>
        <span style={srOnly}>{nf(Math.abs(value))}</span>
        <div style={{ fontSize: '.8rem', color: INK }}>{below.unit}</div>
        <div style={{ fontSize: '.8rem', color: INK, marginTop: 4 }}>{below.note}</div>
      </>
    ) : (
      <div style={{ fontSize: '.8rem', color: INK_FAINT }}>words</div>
    )}
  </div>
);

// ── Words-per-day SVG chart (last 30 days, zero-baseline, signed) ────────────
const WordsPerDayChart: React.FC<{ perDay: DayDelta[] }> = ({ perDay }) => {
  const H = 160, mid = H / 2, slot = 18, barW = 11, w = perDay.length * slot;
  const maxAbs = Math.max(1, ...perDay.map(d => Math.abs(d.delta)));
  const scale = (mid - 14) / maxAbs;
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
      <svg width={w} height={H} role="img" aria-label="Words written per day, last 30 days" style={{ display: 'block' }}>
        <line x1={0} y1={mid} x2={w} y2={mid} stroke={BORDER} strokeWidth={1} />
        {perDay.map((d, i) => {
          const h = Math.abs(d.delta) * scale;
          const x = i * slot + (slot - barW) / 2;
          const up = d.delta >= 0;
          const y = up ? mid - h : mid;
          const isToday = i === perDay.length - 1;
          return (
            <rect key={d.date} x={x} y={y} width={barW} height={Math.max(h, d.delta !== 0 ? 1.5 : 0)}
              rx={2} fill={up ? (isToday ? ACCENT : GREEN) : NEG} opacity={d.delta === 0 ? 0.25 : 1}>
              <title>{`${d.date}: ${d.delta >= 0 ? '+' : ''}${signed(d.delta)} words`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const WritingDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useEmbedTheme();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isReadonly = params.get('readonly') === 'true';
  const urlBookId = params.get('bookId');

  const [books, setBooks] = useState<BookLite[]>([]);
  const [booksLoaded, setBooksLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet('/books')
      .then(r => r.body.json() as unknown as Promise<BookLite[]>)
      .then(list => {
        if (cancelled) return;
        const sorted = [...(list ?? [])].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
        setBooks(sorted);
        setBooksLoaded(true);
      })
      .catch(() => { if (!cancelled) setBooksLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const selectedBookId = urlBookId ?? (books[0] ? bareId(books[0].entityId) : null);
  const currentBook = books.find(b => bareId(b.entityId) === selectedBookId);

  const { activity, loading, error, saveGoal } = useWritingActivity(selectedBookId);
  const metrics = useMemo(() => computeMetrics(activity), [activity]);

  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  const commitGoal = () => {
    const n = Math.max(0, Math.min(100000, parseInt(goalDraft, 10) || 0));
    saveGoal(n);
    setEditingGoal(false);
  };

  const selectBook = (id: string) => {
    const p = new URLSearchParams(location.search);
    p.set('bookId', id);
    navigate({ pathname: location.pathname, search: p.toString() });
  };

  // A negative width is invalid CSS: the browser would drop it and draw the bar full.
  const goalPct = metrics.goal ? Math.max(0, Math.min(100, Math.round((metrics.todaySigned / metrics.goal) * 100))) : 0;
  const belowStart = metrics.todaySigned < 0;

  if (!selectedBookId) {
    return (
      <div className="writing-dashboard" data-theme={theme}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>
          <Helmet><title>Writing Dashboard | Chaptery</title></Helmet>
          <h1 style={{ fontSize: '1.6rem', margin: '0 0 8px', color: INK }}>Writing Dashboard</h1>
          <p style={{ color: INK_SOFT }}>{booksLoaded ? 'No books yet — start writing in Chaptery, then your progress shows up here.' : 'Loading your books…'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="writing-dashboard" data-theme={theme}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: isReadonly ? '20px 16px 40px' : '28px 20px 48px' }}>
        <Helmet><title>Writing Dashboard{currentBook ? ` | ${currentBook.title}` : ''} | Chaptery</title></Helmet>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
          <div style={{
            width: 64, height: 88, borderRadius: 10, flex: 'none', overflow: 'hidden',
            background: SURFACE, border: `1px solid ${BORDER}`, boxShadow: '0 2px 10px rgba(0,0,0,.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {currentBook?.coverUrl
              ? <img src={currentBook.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: '.58rem', fontWeight: 700, color: INK_FAINT, textAlign: 'center', padding: 6, lineHeight: 1.2 }}>{currentBook?.title ?? ''}</span>}
          </div>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ ...labelStyle, color: ACCENT }}>Writing Dashboard</div>
            <h1 style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.12, margin: '6px 0 0', color: INK, overflowWrap: 'anywhere' }}>
              {currentBook?.title ?? 'Your Book'}
            </h1>
          </div>
          {!isReadonly && books.length > 0 && (
            <select
              value={selectedBookId}
              onChange={e => selectBook(e.target.value)}
              className="admin-search-input"
              style={{ width: 'auto', maxWidth: 260 }}
            >
              {books.map(b => <option key={b.entityId} value={bareId(b.entityId)}>{b.title || 'Untitled'}</option>)}
            </select>
          )}
        </div>

        {error && <div className="feedback-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          {/* Signed, not floored: a 0 on a day below where it started looked like writing
              that was not being counted (#1047). Week and month add up days floored at 0,
              so they are never negative. */}
          {belowStart
            ? <StatCard label="Today" value={metrics.todaySigned} accent={NEG}
                below={{ unit: 'words below where you started today', note: 'New words count once you pass it.' }} />
            : <StatCard label="Today" value={metrics.todaySigned} accent={ACCENT} />}
          <StatCard label="This Week" value={metrics.week} />
          <StatCard label="This Month" value={metrics.month} />
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ ...cardStyle, flex: '2 1 320px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 7 }}>
                <Target size={14} /> Daily goal
              </div>
              {!editingGoal ? (
                <button className="btn-secondary" style={{ fontSize: '.78rem', width: 'auto', padding: '3px 10px' }}
                  onClick={() => { setGoalDraft(metrics.goal ? String(metrics.goal) : ''); setEditingGoal(true); }}>
                  <PencilLine size={12} /> {metrics.goal ? 'Edit' : 'Set goal'}
                </button>
              ) : (
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="number" min={0} max={100000} autoFocus value={goalDraft}
                    onChange={e => setGoalDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitGoal(); if (e.key === 'Escape') setEditingGoal(false); }}
                    className="admin-search-input" style={{ width: 90 }} placeholder="words/day" />
                  <button className="btn-primary" style={{ fontSize: '.78rem', width: 'auto', padding: '4px 10px' }} onClick={commitGoal}><Check size={13} /></button>
                </span>
              )}
            </div>

            {metrics.goal ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '12px 0 8px' }}>
                  <span style={{ fontSize: '1.9rem', fontWeight: 700, color: metrics.goalMetToday ? GREEN : belowStart ? NEG : INK, fontVariantNumeric: 'tabular-nums' }}>{signed(metrics.todaySigned)}</span>
                  <span style={{ color: INK_FAINT }}>/ {nf(metrics.goal)} words today</span>
                  {metrics.goalMetToday && <span style={{ color: GREEN, fontWeight: 600, fontSize: '.85rem' }}>✓ met</span>}
                </div>
                <div style={{ height: 10, borderRadius: 6, background: TRACK, overflow: 'hidden' }}>
                  <div data-testid="goal-bar" style={{ height: '100%', width: `${goalPct}%`, background: metrics.goalMetToday ? GREEN : ACCENT, borderRadius: 6, transition: 'width .3s' }} />
                </div>
              </>
            ) : (
              <p style={{ color: INK_SOFT, fontSize: '.9rem', margin: '12px 0 0' }}>Set a daily word goal to track your progress and build a streak.</p>
            )}
          </div>

          <div style={{ ...cardStyle, flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 7 }}>
              <Flame size={14} /> Streak
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 'auto' }}>
              <span style={{ fontSize: '2.1rem', fontWeight: 700, color: metrics.streak > 0 ? NEG : INK, fontVariantNumeric: 'tabular-nums' }}>{metrics.streak}</span>
              <span style={{ color: INK_FAINT }}>day{metrics.streak === 1 ? '' : 's'}</span>
            </div>
            <div style={{ fontSize: '.8rem', color: INK_FAINT }}>{metrics.goal ? 'meeting your goal' : 'writing each day'}</div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>Words per day · last 30 days</div>
          {loading && activity.startTotal === null && Object.keys(activity.days).length === 0
            ? <p style={{ color: INK_FAINT, fontSize: '.9rem' }}>Loading…</p>
            : <WordsPerDayChart perDay={metrics.perDay} />}
        </div>
      </div>
    </div>
  );
};

export default WritingDashboardPage;
